/**
 * THE ROUTE ERROR BOUNDARY.
 *
 * Without an `errorElement`, React Router renders its own developer page: "Unexpected Application
 * Error", a stack trace, and the line "You can provide a way better UX than this when your app throws
 * errors". It is right. A participant who has just pressed Withdraw and is shown a stack trace has no
 * idea whether their money moved, and the one thing they need to know is that it did not.
 *
 * This is the LAST line, not the first. Every screen in this app already handles its own refusals and
 * its own failed loads, because a refusal is a result and belongs in the flow. What reaches here is a
 * programming error -- the withdraw screen throwing `SyntaxError: Cannot convert 22,000 to a BigInt`
 * out of a `useMemo` during render, which is exactly what it did -- and the only honest thing to say
 * about one of those is that it is ours, not theirs.
 *
 * THREE RULES:
 *
 *   1. NO STACK TRACE IN PRODUCTION. `import.meta.env.DEV` gates the technical detail. In production
 *      it is not rendered at all, rather than hidden with CSS.
 *   2. SAY WHERE THE MONEY IS. "Nothing has moved" is the sentence that matters, and it is TRUE by
 *      construction: a render that threw never reached the pipeline, and the pipeline is transactional
 *      when it is reached.
 *   3. GIVE A WAY OUT that is not the browser's back button.
 */

import { Link, isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

export function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  // Shown only in development. `error.message` on a database or API error can quote personal data
  // (the backend's own observability layer refuses exception MESSAGES for exactly this reason), so it
  // is never rendered to a participant.
  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : String(error);

  return (
    <div className="mx-auto max-w-md px-4 py-12 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning/15 text-warning">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 9v4m0 4h.01M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20.6h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
          />
        </svg>
      </span>

      <h1 className="mt-4 text-lg font-semibold text-ink">
        {is404 ? 'We could not find that page' : 'Something went wrong on our side'}
      </h1>

      <p className="mx-auto mt-2 max-w-sm text-sm text-ink-muted">
        {is404
          ? 'The link may be old, or the page may have moved.'
          : 'This is a fault in the app, not in your account. Nothing has moved and your balance is untouched.'}
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="rounded-field border border-border bg-surface px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Go back
        </button>
        <Link
          to="/home"
          className="rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
        >
          Back to home
        </Link>
      </div>

      {!is404 && (
        <p className="mt-6 text-xs text-ink-subtle">
          If this keeps happening,{' '}
          <Link to="/support" className="text-brand underline">
            tell us about it
          </Link>{' '}
          and we will look into it.
        </p>
      )}

      {import.meta.env.DEV && !is404 ? (
        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs text-ink-subtle">
            Technical detail (development only)
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-card bg-surface-sunken p-3 text-left text-micro text-ink-muted">
            {detail}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
